/**
 * Tier Criteria
 *
 * Single source of truth for risk tier definitions.
 * Used by frontend (auto-display based on slider values) and backend (AI prompt filtering).
 */

// =============================================================================
// Types
// =============================================================================

export type RiskLevel = 'none' | 'low' | 'medium' | 'high' | 'very_high';

export interface TierCriteria {
  key: string;
  name: string;
  minLegs: number;
  maxLegs: number;
  minOdds: number; // American format (e.g., 200 = +200)
  maxOdds: number; // Infinity for no cap
  edgeLabel: string; // Human-readable edge strength (e.g., "8%+")
  risk: RiskLevel;
  promptGuideline: string; // Full guideline text for AI prompt
  proOnly?: boolean; // If true, only available to Pro+ users
}

// =============================================================================
// Tier Definitions
// =============================================================================

export const TIER_CRITERIA: TierCriteria[] = [
  {
    key: 'safe',
    name: 'Safe Play',
    minLegs: 2,
    maxLegs: 2,
    minOdds: 200,
    maxOdds: 300,
    edgeLabel: '8%+',
    risk: 'none',
    promptGuideline:
      '"Safe Play" (2 legs, ~+200-300 odds) - Strong edges (8%+), NO risk factors, hitEstimate 4+/5',
  },
  {
    key: 'value',
    name: 'Value Play',
    minLegs: 2,
    maxLegs: 3,
    minOdds: 250,
    maxOdds: 400,
    edgeLabel: '5-8%',
    risk: 'low',
    promptGuideline:
      '"Value Play" (2-3 legs, ~+250-400 odds) - Good edges (5-8%), minor risk factors OK (severity: low), hitEstimate 3+/5',
  },
  {
    key: 'upside',
    name: 'Upside Play',
    minLegs: 3,
    maxLegs: 3,
    minOdds: 350,
    maxOdds: 550,
    edgeLabel: '3-5%',
    risk: 'medium',
    promptGuideline:
      '"Upside Play" (3 legs, ~+350-550 odds) - Moderate edges (3-5%) OR risk factors (severity: medium), hitEstimate 2+/5',
  },
  {
    key: 'long',
    name: 'Long Shot',
    minLegs: 3,
    maxLegs: 4,
    minOdds: 500,
    maxOdds: 800,
    edgeLabel: '0-3%',
    risk: 'high',
    promptGuideline:
      '"Long Shot" (3-4 legs, ~+500-800 odds) - Slight edges (0-3%) OR hitEstimate 1/5 OR major risk factors (severity: high)',
  },
  {
    key: 'lottery',
    name: 'Lottery Ticket',
    minLegs: 5,
    maxLegs: 8,
    minOdds: 1000,
    maxOdds: Infinity,
    edgeLabel: 'Any',
    risk: 'very_high',
    promptGuideline:
      '"Lottery Ticket" (5-8 legs, +1000+ odds) - Very high risk, massive potential payout. Any edge strength. Go for big combos with high variance legs. hitEstimate 0-1/5',
    proOnly: true,
  },
];

// =============================================================================
// Utilities
// =============================================================================

/**
 * Get tiers that overlap with the given leg count and odds ranges.
 * A tier is included when BOTH its leg range and odds range overlap with the user's selection.
 */
export function getIncludedTiers(
  minLegs: number,
  maxLegs: number,
  minOdds: number,
  maxOdds: number,
): TierCriteria[] {
  return TIER_CRITERIA.filter((tier) => {
    const legsOverlap = minLegs <= tier.maxLegs && tier.minLegs <= maxLegs;
    const oddsOverlap = minOdds <= tier.maxOdds && tier.minOdds <= maxOdds;
    return legsOverlap && oddsOverlap;
  });
}

/**
 * Get tier criteria by key.
 */
export function getTierByKey(key: string): TierCriteria | undefined {
  return TIER_CRITERIA.find((t) => t.key === key);
}

/**
 * Build AI prompt tier guidelines for the included tiers only.
 * When no tiers match the slider range, returns a fallback instruction
 * so the AI still knows how to categorize parlays.
 */
export function buildTierGuidelines(includedTiers: TierCriteria[]): string {
  if (includedTiers.length === 0) {
    return '- Use your best judgment to categorize parlays as "Safe Play", "Value Play", "Upside Play", or "Long Shot" based on edge strength and risk factors. The user\'s leg count and odds range may not match standard tier definitions — prioritize the configured ranges over tier defaults.';
  }
  const lines = includedTiers.map((t) => `- ${t.promptGuideline}`);
  return lines.join('\n');
}
